import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../lib/theme";
import { Sparkles, Check, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const ONBOARD_KEY = "rc_onboarded_v1";

export function OnboardingModal() {
    const { themeId, setTheme, themes } = useTheme();
    const [open, setOpen] = useState(false);
    const [choice, setChoice] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem(ONBOARD_KEY)) {
            // small delay so the page can render behind it
            const t = setTimeout(() => setOpen(true), 700);
            return () => clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        setChoice(themeId);
    }, [themeId]);

    const finish = (pick) => {
        if (pick) setTheme(pick);
        localStorage.setItem(ONBOARD_KEY, "1");
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-background/85 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="onboarding-modal"
                >
                    <motion.div
                        className="w-full max-w-5xl card-surface rounded-3xl p-6 md:p-10 relative overflow-hidden"
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {/* Glow */}
                        <div
                            aria-hidden
                            className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-40 blur-3xl pointer-events-none"
                            style={{ background: "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)" }}
                        />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-5">
                                <Sparkles className="h-3 w-3 text-primary" />
                                Welcome to Real-Check
                            </div>
                            <h2 className="font-display text-3xl md:text-5xl text-accent tracking-tight leading-tight max-w-2xl">
                                Pick your <span className="text-primary">vibe</span>.
                            </h2>
                            <p className="text-muted-foreground mt-3 max-w-xl">
                                Three seconds. Pick the look that feels right for you. Change it anytime from the palette icon.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mt-8">
                                {Object.values(themes).map((t, i) => {
                                    const isChosen = choice === t.id;
                                    return (
                                        <motion.button
                                            key={t.id}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.35, delay: 0.08 + i * 0.04 }}
                                            onClick={() => { setChoice(t.id); setTheme(t.id); }}
                                            data-testid={`onboard-theme-${t.id}`}
                                            className={`text-left rounded-2xl overflow-hidden border transition-all group ${
                                                isChosen ? "border-primary scale-[1.02]" : "border-white/10 hover:border-white/30 hover:-translate-y-0.5"
                                            }`}
                                        >
                                            <div
                                                className="relative h-24 p-3"
                                                style={{ background: t.preview.bg, backgroundImage: t.heroGlow }}
                                            >
                                                <div className="flex gap-1.5 mb-2">
                                                    <span className="w-4 h-4 rounded-full" style={{ background: t.preview.primary, boxShadow: `0 0 12px ${t.preview.primary}99` }} />
                                                    <span className="w-4 h-4 rounded-full" style={{ background: t.preview.accent, opacity: 0.85 }} />
                                                </div>
                                                <div
                                                    style={{ color: t.preview.accent, fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 13, letterSpacing: -0.3 }}
                                                >
                                                    Truth,{" "}
                                                    <span style={{ color: t.preview.primary }}>verified</span>.
                                                </div>
                                                {isChosen && (
                                                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                                        <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-3">
                                                <div className="text-sm font-semibold text-accent">{t.name}</div>
                                                <div className="text-[11px] text-muted-foreground line-clamp-1">{t.tagline}</div>
                                            </div>
                                        </motion.button>
                                    );
                                })}
                            </div>

                            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                                <button
                                    onClick={() => finish(null)}
                                    className="text-sm text-muted-foreground hover:text-accent transition-colors"
                                    data-testid="onboard-skip"
                                >
                                    Skip for now
                                </button>
                                <Button
                                    onClick={() => finish(choice)}
                                    data-testid="onboard-continue"
                                    className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30"
                                >
                                    Let&apos;s go <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
